import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Button, Typography, Paper, Stack, IconButton, TextField, Checkbox, FormControlLabel, CircularProgress, Alert } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleIcon from '@mui/icons-material/AddCircle'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

const DispoPublicPage = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [expired, setExpired] = useState(false)
  const [data, setData] = useState(null)
  const [slots, setSlots] = useState([{ start_time: '08:00', end_time: '18:00' }])
  const [carAvailable, setCarAvailable] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/availability-response?token=${token}`,
          { headers: { apikey: SUPABASE_ANON_KEY } }
        )

        if (res.status === 404) {
          setError('Ce lien n\'est pas valide.')
          return
        }

        const json = await res.json()

        if (json.expired) {
          setExpired(true)
          return
        }

        setData(json)

        if (json.responses?.length > 0) {
          setSlots(json.responses.map(r => ({
            start_time: r.start_time.slice(0, 5),
            end_time: r.end_time.slice(0, 5),
          })))
          setCarAvailable(json.responses[0].car_available)
        }
      } catch (err) {
        setError('Erreur de connexion.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [token])

  const handleAddSlot = () => {
    setSlots(prev => [...prev, { start_time: '08:00', end_time: '18:00' }])
  }

  const handleRemoveSlot = (index) => {
    setSlots(prev => prev.filter((_, i) => i !== index))
  }

  const handleSlotChange = (index, field, value) => {
    setSlots(prev => prev.map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot
    ))
  }

  const handleSubmit = async () => {
    if (slots.length === 0) return

    setSaving(true)
    setError(null)

    try {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/availability-response`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token, slots, car_available: carAvailable }),
        }
      )

      if (!res.ok) {
        const json = await res.json()
        setError(json.error || 'Erreur lors de l\'enregistrement.')
        return
      }

      setSuccess(true)
    } catch (err) {
      setError('Erreur de connexion.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (expired) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Alert severity="info">Cette demande de disponibilité est expirée.</Alert>
      </Box>
    )
  }

  if (success) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Alert severity="success">Tes disponibilités ont été enregistrées.</Alert>
      </Box>
    )
  }

  const formattedDate = data?.date
    ? new Date(data.date + 'T00:00:00').toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      })
    : ''

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', p: 2, pt: 6, backgroundColor: '#f5f5f5' }}>
      <Paper sx={{ p: 4, maxWidth: 500, width: '100%', height: 'fit-content' }}>
        <Typography variant="h5" gutterBottom>
          Disponibilités
        </Typography>

        <Typography variant="body1" gutterBottom>
          Bonjour {data?.profile?.first_name}, merci d'indiquer tes créneaux pour le :
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {formattedDate}
        </Typography>

        <Stack spacing={2} sx={{ mt: 3 }}>
          {slots.map((slot, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                label="De"
                type="time"
                value={slot.start_time}
                onChange={(e) => handleSlotChange(index, 'start_time', e.target.value)}
                size="small"
                sx={{ width: 130 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <TextField
                label="À"
                type="time"
                value={slot.end_time}
                onChange={(e) => handleSlotChange(index, 'end_time', e.target.value)}
                size="small"
                sx={{ width: 130 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              {slots.length > 1 && (
                <IconButton onClick={() => handleRemoveSlot(index)} color="error" size="small">
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>

        <Button
          startIcon={<AddCircleIcon />}
          onClick={handleAddSlot}
          size="small"
          sx={{ mt: 1 }}
        >
          Ajouter un créneau
        </Button>

        {data?.profile?.has_car && (
          <FormControlLabel
            control={
              <Checkbox
                checked={carAvailable}
                onChange={(e) => setCarAvailable(e.target.checked)}
              />
            }
            label="Ma voiture est disponible"
            sx={{ mt: 2, display: 'block' }}
          />
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || slots.length === 0}
          fullWidth
          sx={{ mt: 3 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Valider'}
        </Button>
      </Paper>
    </Box>
  )
}

export default DispoPublicPage
